import React, { useEffect, useState, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Brain, Mic, Volume2, ChevronDown, ChevronUp, Check, Star,
  Eye, EyeOff, Save, Loader2, BadgeCheck, Zap, Server, Plus, Trash2, X,
  Type, ImageIcon, Sparkles, KeyRound, Settings2
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';

const CapPill = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all border',
      active
        ? 'bg-foreground text-background border-foreground shadow-sm'
        : 'bg-background text-muted-foreground border-border hover:border-foreground/30 hover:text-foreground'
    )}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </button>
);

// ── ServiceCard used by AI Models, TTS, STT tabs ────────────────────────────
const ServiceCard = ({ item, type, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showOverrides, setShowOverrides] = useState(false);
  const [form, setForm] = useState({
    api_key: '',
    system_prompt: item.system_prompt || '',
    text_prompt: item.text_prompt || '',
    image_prompt: item.image_prompt || '',
    moderate_relax_prompt: item.moderate_relax_prompt || '',
    temperature: item.temperature ?? 0.7,
    max_tokens: item.max_tokens ?? 1000,
    voice: item.voice || '',
    language: item.language || '',
    enabled: item.enabled,
    is_default: item.is_default,
    for_text: item.for_text !== false,
    for_image: item.for_image === true,
    is_default_image: item.is_default_image === true,
  });

  const prevSnap = useRef('');
  useEffect(() => {
    const snap = JSON.stringify({
      id: item.id,
      system_prompt: item.system_prompt || '',
      text_prompt: item.text_prompt || '',
      image_prompt: item.image_prompt || '',
      moderate_relax_prompt: item.moderate_relax_prompt || '',
      temperature: item.temperature ?? 0.7,
      max_tokens: item.max_tokens ?? 1000,
      voice: item.voice || '',
      language: item.language || '',
      enabled: item.enabled,
      is_default: item.is_default,
      for_text: item.for_text !== false,
      for_image: item.for_image === true,
      is_default_image: item.is_default_image === true,
    });
    if (snap === prevSnap.current) return;
    prevSnap.current = snap;
    setForm((f) => ({
      ...f,
      api_key: '',
      system_prompt: item.system_prompt || '',
      text_prompt: item.text_prompt || '',
      image_prompt: item.image_prompt || '',
      moderate_relax_prompt: item.moderate_relax_prompt || '',
      temperature: item.temperature ?? 0.7,
      max_tokens: item.max_tokens ?? 1000,
      voice: item.voice || '',
      language: item.language || '',
      enabled: item.enabled,
      is_default: item.is_default,
      for_text: item.for_text !== false,
      for_image: item.for_image === true,
      is_default_image: item.is_default_image === true,
    }));
  }, [item]);

  const handleSave = async () => {
    if (type === 'ai_models' && form.enabled && !form.for_text && !form.for_image) {
      toast.error('Turn on Text and/or Image for this model');
      return;
    }
    setSaving(true);
    try {
      await onSave(item.id, { ...form });
      toast.success(`${item.name} updated`);
      if (form.api_key) setForm(f => ({ ...f, api_key: '' }));
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isAiModel = type === 'ai_models';
  const isTts = type === 'tts_services';
  const showText = !isAiModel || form.for_text;
  const showImage = isAiModel && form.for_image;

  if (isAiModel) {
    return (
      <div
        data-testid={`service-card-${item.id}`}
        className={cn(
          'group relative overflow-hidden rounded-2xl border transition-all duration-200',
          form.enabled
            ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.06] to-transparent shadow-sm'
            : 'border-border/80 bg-card hover:border-border'
        )}
      >
        <div className="flex items-start gap-4 p-4 sm:p-5">
          <div
            className={cn(
              'mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-semibold',
              form.enabled
                ? 'bg-emerald-500 text-white'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {(item.name || '?').slice(0, 1)}
          </div>

          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-foreground">{item.name}</h3>
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {item.provider}
                  </span>
                  {item.has_api_key && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                      <BadgeCheck className="h-3.5 w-3.5" /> Key
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{item.model_id}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="flex flex-col items-end gap-0.5">
                  <Switch
                    data-testid={`toggle-${item.id}`}
                    checked={form.enabled}
                    onCheckedChange={(v) => setForm(f => ({ ...f, enabled: v }))}
                  />
                  <span className="text-[10px] text-muted-foreground">{form.enabled ? 'On' : 'Off'}</span>
                </div>
                <button
                  data-testid={`expand-${item.id}`}
                  onClick={() => setExpanded(e => !e)}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <Settings2 className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <CapPill
                testId={`for-text-${item.id}`}
                active={form.for_text}
                icon={Type}
                label="Text"
                onClick={() => setForm(f => ({
                  ...f,
                  for_text: !f.for_text,
                  is_default: !f.for_text ? f.is_default : false,
                }))}
              />
              <CapPill
                testId={`for-image-${item.id}`}
                active={form.for_image}
                icon={ImageIcon}
                label="Image"
                onClick={() => setForm(f => ({
                  ...f,
                  for_image: !f.for_image,
                  is_default_image: !f.for_image ? f.is_default_image : false,
                }))}
              />
              {form.for_text && (
                <button
                  type="button"
                  data-testid={`default-${item.id}`}
                  onClick={() => setForm(f => ({ ...f, is_default: !f.is_default }))}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-medium border transition-colors',
                    form.is_default
                      ? 'border-amber-400/60 bg-amber-400/15 text-amber-700 dark:text-amber-300'
                      : 'border-transparent text-muted-foreground hover:bg-muted'
                  )}
                >
                  <Star className={cn('h-3 w-3', form.is_default && 'fill-current')} />
                  Default text
                </button>
              )}
              {form.for_image && (
                <button
                  type="button"
                  data-testid={`default-image-${item.id}`}
                  onClick={() => setForm(f => ({ ...f, is_default_image: !f.is_default_image }))}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-medium border transition-colors',
                    form.is_default_image
                      ? 'border-sky-400/60 bg-sky-400/15 text-sky-700 dark:text-sky-300'
                      : 'border-transparent text-muted-foreground hover:bg-muted'
                  )}
                >
                  <Star className={cn('h-3 w-3', form.is_default_image && 'fill-current')} />
                  Default image
                </button>
              )}
            </div>
          </div>
        </div>

        {expanded && (
          <div className="space-y-4 border-t border-border/70 bg-background/60 px-4 py-4 sm:px-5">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs font-medium">
                <KeyRound className="h-3.5 w-3.5" />
                API key {item.has_api_key && <span className="font-normal text-emerald-600">· already saved</span>}
              </Label>
              <div className="relative">
                <Input
                  data-testid={`api-key-${item.id}`}
                  type={showKey ? 'text' : 'password'}
                  placeholder={item.has_api_key ? 'Paste new key to replace…' : 'Paste API key…'}
                  value={form.api_key}
                  onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                  className="pr-10 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(v => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Temperature</Label>
                <Input
                  type="number" step="0.1" min="0" max="2"
                  value={form.temperature}
                  onChange={e => setForm(f => ({ ...f, temperature: parseFloat(e.target.value) }))}
                  className="text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max tokens</Label>
                <Input
                  type="number"
                  value={form.max_tokens}
                  onChange={e => setForm(f => ({ ...f, max_tokens: parseInt(e.target.value) }))}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="rounded-xl border border-dashed border-border bg-muted/20 p-3">
              <button
                type="button"
                className="flex w-full items-center justify-between text-left text-xs font-medium"
                onClick={() => setShowOverrides(v => !v)}
              >
                <span className="text-muted-foreground">Advanced · override shared prompts</span>
                {showOverrides ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {showOverrides && (
                <div className="mt-3 space-y-3">
                  <p className="text-[11px] text-muted-foreground">
                    Leave blank to keep using Shared prompts. Only fill if this model needs different instructions.
                  </p>
                  {showText && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs">Chat override</Label>
                        <Textarea rows={2} value={form.system_prompt} onChange={e => setForm(f => ({ ...f, system_prompt: e.target.value }))} className="text-sm resize-none" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Nutrition override</Label>
                        <Textarea rows={2} value={form.text_prompt} onChange={e => setForm(f => ({ ...f, text_prompt: e.target.value }))} className="text-sm resize-none" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Moderate relax override</Label>
                        <Textarea rows={2} value={form.moderate_relax_prompt} onChange={e => setForm(f => ({ ...f, moderate_relax_prompt: e.target.value }))} className="text-sm resize-none" />
                      </div>
                    </>
                  )}
                  {showImage && (
                    <div className="space-y-1">
                      <Label className="text-xs">Image override</Label>
                      <Textarea rows={2} value={form.image_prompt} onChange={e => setForm(f => ({ ...f, image_prompt: e.target.value }))} className="text-sm resize-none" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button data-testid={`save-${item.id}`} onClick={handleSave} disabled={saving} className="w-full rounded-xl">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save model
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={`service-card-${item.id}`}
      className={cn(
        'overflow-hidden rounded-2xl border transition-all',
        item.enabled ? 'border-primary/25 bg-primary/[0.04]' : 'border-border bg-card'
      )}
    >
      <div className="flex items-center gap-3 p-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{item.name}</span>
            {item.provider && <Badge variant="outline" className="text-xs">{item.provider}</Badge>}
            {item.is_default && (
              <Badge className="border-primary/20 bg-primary/10 text-xs text-primary">
                <Star className="mr-1 h-3 w-3" /> Default
              </Badge>
            )}
            {item.has_api_key && (
              <Badge variant="outline" className="border-emerald-300 text-xs text-emerald-600">
                <BadgeCheck className="mr-1 h-3 w-3" /> Key set
              </Badge>
            )}
          </div>
        </div>
        <Switch
          data-testid={`toggle-${item.id}`}
          checked={form.enabled}
          onCheckedChange={(v) => setForm(f => ({ ...f, enabled: v }))}
        />
        <button
          data-testid={`expand-${item.id}`}
          onClick={() => setExpanded(e => !e)}
          className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-border px-4 pb-4 pt-3">
          <div className="space-y-1">
            <Label className="text-xs">API Key {item.has_api_key && <span className="text-emerald-600">(already set)</span>}</Label>
            <div className="relative">
              <Input
                data-testid={`api-key-${item.id}`}
                type={showKey ? 'text' : 'password'}
                placeholder={item.has_api_key ? 'Enter new key to replace…' : 'Enter API key…'}
                value={form.api_key}
                onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                className="pr-9 text-sm"
              />
              <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid={`default-${item.id}`} checked={form.is_default} onCheckedChange={(v) => setForm(f => ({ ...f, is_default: v }))} />
            <Label className="cursor-pointer text-xs">Set as default</Label>
          </div>
          {isTts && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Voice</Label>
                <Input value={form.voice} onChange={e => setForm(f => ({ ...f, voice: e.target.value }))} className="text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Language</Label>
                <Input value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} className="text-sm" />
              </div>
            </div>
          )}
          <Button data-testid={`save-${item.id}`} onClick={handleSave} disabled={saving} size="sm" className="w-full">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

const ServiceSection = ({ items, type, onSave }) => (
  <div className="space-y-3">
    {items.map(item => (
      <ServiceCard key={item.id || item._id} item={item} type={type} onSave={onSave} />
    ))}
    {items.length === 0 && (
      <div className="py-12 text-center text-muted-foreground">
        <Zap className="mx-auto mb-2 h-8 w-8 opacity-30" />
        <p className="text-sm">No services configured</p>
      </div>
    )}
  </div>
);

const SHARED_PROMPT_TABS = [
  { id: 'system_prompt', label: 'Chat', hint: 'Used for AI chat replies' },
  { id: 'text_prompt', label: 'Nutrition', hint: 'Used for macros / food text' },
  { id: 'image_prompt', label: 'Image', hint: 'Used when reading food photos' },
  { id: 'moderate_relax_prompt', label: 'Moderate', hint: 'Moderate / relax diet flow' },
];

/** Company-wide prompts — paste once, all models inherit. */
const SharedPromptsCard = ({ value, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('system_prompt');
  const [form, setForm] = useState({
    system_prompt: value?.system_prompt || '',
    text_prompt: value?.text_prompt || '',
    image_prompt: value?.image_prompt || '',
    moderate_relax_prompt: value?.moderate_relax_prompt || '',
  });

  useEffect(() => {
    setForm({
      system_prompt: value?.system_prompt || '',
      text_prompt: value?.text_prompt || '',
      image_prompt: value?.image_prompt || '',
      moderate_relax_prompt: value?.moderate_relax_prompt || '',
    });
  }, [value]);

  const filledCount = SHARED_PROMPT_TABS.filter((t) => String(form[t.id] || '').trim()).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      toast.success('Shared prompts saved');
    } catch {
      toast.error('Failed to save shared prompts');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex items-start gap-4 border-b border-border bg-gradient-to-r from-primary/12 via-sky-500/5 to-transparent p-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md shadow-primary/25">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">1</span>
            <h2 className="text-base font-semibold text-foreground">Shared prompts</h2>
            <Badge variant="secondary" className="rounded-full text-[11px]">{filledCount}/4 filled</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Write instructions once. Every model uses these — no copy-paste per model.
          </p>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <div className="mb-4 flex gap-1.5 overflow-x-auto pb-1">
          {SHARED_PROMPT_TABS.map((t) => {
            const filled = String(form[t.id] || '').trim().length > 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={cn(
                  'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors',
                  activeTab === t.id
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                )}
              >
                {t.label}
                {filled ? ' ✓' : ''}
              </button>
            );
          })}
        </div>

        {SHARED_PROMPT_TABS.map((t) => (
          activeTab === t.id ? (
            <div key={t.id} className="space-y-2">
              <p className="text-xs text-muted-foreground">{t.hint}</p>
              <Textarea
                rows={8}
                value={form[t.id]}
                onChange={(e) => setForm((f) => ({ ...f, [t.id]: e.target.value }))}
                className="min-h-[160px] resize-y rounded-xl text-sm"
                placeholder={`Paste your ${t.label.toLowerCase()} prompt here…`}
              />
            </div>
          ) : null
        ))}

        <Button onClick={handleSave} disabled={saving} className="mt-4 w-full rounded-xl sm:w-auto">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save shared prompts
        </Button>
      </div>
    </section>
  );
};

// ── GemmaServerCard ──────────────────────────────────────────────────────────
const GemmaServerCard = ({ server, onSave, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    name: server.name || '',
    api_url: server.api_url || '',
    api_key: '',
    system_prompt: server.system_prompt || '',
    text_prompt: server.text_prompt || '',
    image_prompt: server.image_prompt || '',
    moderate_relax_prompt: server.moderate_relax_prompt || '',
    enabled: server.enabled || false,
    is_default: server.is_default || false,
  });

  useEffect(() => {
    setForm({
      name: server.name || '',
      api_url: server.api_url || '',
      api_key: '',
      system_prompt: server.system_prompt || '',
      text_prompt: server.text_prompt || '',
      image_prompt: server.image_prompt || '',
      moderate_relax_prompt: server.moderate_relax_prompt || '',
      enabled: server.enabled || false,
      is_default: server.is_default || false,
    });
  }, [server]);

  const handleSave = async () => {
    if (!form.api_url.trim()) { toast.error('API URL is required'); return; }
    setSaving(true);
    try {
      await onSave(server._id, { ...form });
      toast.success(`${form.name || 'Gemma Server'} updated`);
      if (form.api_key) setForm(f => ({ ...f, api_key: '' }));
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    try {
      await onDelete(server._id);
      toast.success('Gemma server deleted');
    } catch {
      toast.error('Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <div
      data-testid={`gemma-card-${server._id}`}
      className={cn(
        'border rounded-xl overflow-hidden transition-all',
        form.enabled ? 'border-violet-400/40 bg-violet-50/30 dark:bg-violet-950/20' : 'border-border bg-card'
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4">
        <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center shrink-0">
          <Server className="w-4 h-4 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-foreground">{form.name || 'Unnamed Server'}</span>
            {form.is_default && (
              <Badge className="text-xs bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/40 dark:text-violet-300">
                <Star className="w-3 h-3 mr-1" /> Default
              </Badge>
            )}
            {form.enabled && (
              <Badge variant="secondary" className="text-xs">
                <Check className="w-3 h-3 mr-1" /> Enabled
              </Badge>
            )}
            {server.has_api_key && (
              <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                <BadgeCheck className="w-3 h-3 mr-1" /> Key set
              </Badge>
            )}
          </div>
          {form.api_url && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{form.api_url}</p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Switch
            data-testid={`gemma-toggle-${server._id}`}
            checked={form.enabled}
            onCheckedChange={(v) => setForm(f => ({ ...f, enabled: v }))}
          />
          <button
            data-testid={`gemma-expand-${server._id}`}
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded fields */}
      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
          {/* Name & API URL */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Server Name</Label>
              <Input
                data-testid={`gemma-name-${server._id}`}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Gemma Dev Server"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">API URL <span className="text-destructive">*</span></Label>
              <Input
                data-testid={`gemma-url-${server._id}`}
                value={form.api_url}
                onChange={e => setForm(f => ({ ...f, api_url: e.target.value }))}
                placeholder="https://gemma01.wingsdiet.com"
                className="text-sm"
              />
            </div>
          </div>

          {/* API Key */}
          <div className="space-y-1">
            <Label className="text-xs">API Key {server.has_api_key && <span className="text-green-600">(already set — enter to replace)</span>}</Label>
            <div className="relative">
              <Input
                data-testid={`gemma-key-${server._id}`}
                type={showKey ? 'text' : 'password'}
                placeholder={server.has_api_key ? 'Enter new key to replace…' : 'Enter API key…'}
                value={form.api_key}
                onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                className="pr-9 text-sm"
              />
              <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Set as default */}
          <div className="flex items-center gap-2">
            <Switch
              data-testid={`gemma-default-${server._id}`}
              checked={form.is_default}
              onCheckedChange={(v) => setForm(f => ({ ...f, is_default: v }))}
            />
            <Label className="text-xs cursor-pointer">Set as default</Label>
          </div>

          {/* Prompts */}
          <div className="space-y-1">
            <Label className="text-xs">Chat Prompt</Label>
            <Textarea
              data-testid={`gemma-system-prompt-${server._id}`}
              rows={3}
              value={form.system_prompt}
              onChange={e => setForm(f => ({ ...f, system_prompt: e.target.value }))}
              placeholder="You are a helpful diet assistant…"
              className="text-sm resize-none"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Nutrition Values Prompt</Label>
            <Textarea
              data-testid={`gemma-text-prompt-${server._id}`}
              rows={3}
              value={form.text_prompt}
              onChange={e => setForm(f => ({ ...f, text_prompt: e.target.value }))}
              placeholder="Analyze the nutritional content of…"
              className="text-sm resize-none"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Image AI Prompt</Label>
            <Textarea
              data-testid={`gemma-image-prompt-${server._id}`}
              rows={3}
              value={form.image_prompt}
              onChange={e => setForm(f => ({ ...f, image_prompt: e.target.value }))}
              placeholder="Identify the food items in this image…"
              className="text-sm resize-none"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Moderate Relax AI Prompt</Label>
            <Textarea
              data-testid={`gemma-moderate-prompt-${server._id}`}
              rows={3}
              value={form.moderate_relax_prompt}
              onChange={e => setForm(f => ({ ...f, moderate_relax_prompt: e.target.value }))}
              placeholder="Evaluate whether this diet plan is…"
              className="text-sm resize-none"
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <Button
              data-testid={`gemma-save-${server._id}`}
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="flex-1"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
            {confirmDelete ? (
              <div className="flex gap-1">
                <Button
                  data-testid={`gemma-confirm-delete-${server._id}`}
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Delete'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                data-testid={`gemma-delete-${server._id}`}
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="text-destructive hover:bg-destructive/10 border-destructive/30"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Add Gemma Server Form ────────────────────────────────────────────────────
const EMPTY_GEMMA = { name: '', api_url: '', api_key: '', system_prompt: '', text_prompt: '', image_prompt: '', moderate_relax_prompt: '' };

const AddGemmaServerForm = ({ onAdd, onCancel }) => {
  const [form, setForm] = useState(EMPTY_GEMMA);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const handleSubmit = async () => {
    if (!form.api_url.trim()) { toast.error('API URL is required'); return; }
    setSaving(true);
    try {
      await onAdd({ ...form });
      toast.success('Gemma server added');
      setForm(EMPTY_GEMMA);
    } catch {
      toast.error('Failed to add server');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 border-dashed border-violet-300 dark:border-violet-700 rounded-xl p-4 space-y-3 bg-violet-50/20 dark:bg-violet-950/10">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
          <Server className="w-4 h-4 text-violet-600" /> New Gemma Server
        </h3>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground p-1 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Server Name</Label>
          <Input
            data-testid="new-gemma-name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Gemma Dev Server"
            className="text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">API URL <span className="text-destructive">*</span></Label>
          <Input
            data-testid="new-gemma-url"
            value={form.api_url}
            onChange={e => setForm(f => ({ ...f, api_url: e.target.value }))}
            placeholder="https://gemma01.wingsdiet.com"
            className="text-sm"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">API Key</Label>
        <div className="relative">
          <Input
            data-testid="new-gemma-key"
            type={showKey ? 'text' : 'password'}
            value={form.api_key}
            onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
            placeholder="Bearer token or API key…"
            className="pr-9 text-sm"
          />
          <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Chat Prompt</Label>
        <Textarea data-testid="new-gemma-system-prompt" rows={2} value={form.system_prompt} onChange={e => setForm(f => ({ ...f, system_prompt: e.target.value }))} placeholder="You are a helpful diet assistant…" className="text-sm resize-none" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Nutrition Values Prompt</Label>
        <Textarea data-testid="new-gemma-text-prompt" rows={2} value={form.text_prompt} onChange={e => setForm(f => ({ ...f, text_prompt: e.target.value }))} placeholder="Analyze the nutritional content…" className="text-sm resize-none" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Image AI Prompt</Label>
        <Textarea data-testid="new-gemma-image-prompt" rows={2} value={form.image_prompt} onChange={e => setForm(f => ({ ...f, image_prompt: e.target.value }))} placeholder="Identify the food items…" className="text-sm resize-none" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Moderate Relax AI Prompt</Label>
        <Textarea data-testid="new-gemma-moderate-prompt" rows={2} value={form.moderate_relax_prompt} onChange={e => setForm(f => ({ ...f, moderate_relax_prompt: e.target.value }))} placeholder="Evaluate whether this diet plan…" className="text-sm resize-none" />
      </div>

      <div className="flex gap-2 pt-1">
        <Button data-testid="new-gemma-submit" onClick={handleSubmit} disabled={saving} size="sm" className="flex-1 bg-violet-600 hover:bg-violet-700 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Add Server
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AIConfigPage() {
  const [config, setConfig] = useState({
    ai_models: [],
    tts_services: [],
    stt_services: [],
    gemma_servers: [],
    shared_prompts: {
      system_prompt: '',
      text_prompt: '',
      image_prompt: '',
      moderate_relax_prompt: '',
    },
  });
  const [loading, setLoading] = useState(true);
  const [showAddGemma, setShowAddGemma] = useState(false);

  const reload = async () => {
    try {
      const r = await companyAdminCustomersAPI.getAiConfig();
      setConfig({
        ai_models: r.data.ai_models || [],
        tts_services: r.data.tts_services || [],
        stt_services: r.data.stt_services || [],
        gemma_servers: r.data.gemma_servers || [],
        shared_prompts: r.data.shared_prompts || {
          system_prompt: '',
          text_prompt: '',
          image_prompt: '',
          moderate_relax_prompt: '',
        },
      });
    } catch {
      toast.error('Failed to load AI config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const makeUpdater = (updateFn) => async (id, form) => {
    await updateFn(id, form);
    await reload();
  };

  const handleSaveSharedPrompts = async (form) => {
    await companyAdminCustomersAPI.updateSharedPrompts(form);
    await reload();
  };

  const handleAddGemma = async (data) => {
    await companyAdminCustomersAPI.createGemmaServer(data);
    setShowAddGemma(false);
    await reload();
  };

  const handleUpdateGemma = async (id, data) => {
    await companyAdminCustomersAPI.updateGemmaServer(id, data);
    await reload();
  };

  const handleDeleteGemma = async (id) => {
    await companyAdminCustomersAPI.deleteGemmaServer(id);
    await reload();
  };

  const stats = useMemo(() => {
    const models = config.ai_models || [];
    const enabled = models.filter((m) => m.enabled);
    return {
      enabled: enabled.length,
      text: enabled.filter((m) => m.for_text !== false).length,
      image: enabled.filter((m) => m.for_image === true).length,
      keyed: models.filter((m) => m.has_api_key).length,
      tts: (config.tts_services || []).filter((s) => s.enabled).length,
      stt: (config.stt_services || []).filter((s) => s.enabled).length,
      gemma: (config.gemma_servers || []).filter((s) => s.enabled).length,
    };
  }, [config]);

  if (loading) {
    return (
      <CompanyAdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </CompanyAdminLayout>
    );
  }

  return (
    <CompanyAdminLayout>
      <div className="relative min-h-full">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-[radial-gradient(ellipse_at_top,_hsl(var(--primary)/0.14),_transparent_60%)]"
        />
        <div className="relative mx-auto max-w-4xl space-y-6 p-6">
          <header className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">WingsDiet · Admin</p>
                <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-foreground">AI Configuration</h1>
                <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                  Two steps: set shared prompts, then turn models on for text and/or image.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {[
                  { label: 'Active', value: stats.enabled },
                  { label: 'Text', value: stats.text },
                  { label: 'Image', value: stats.image },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="min-w-[72px] rounded-2xl border border-border/80 bg-background/80 px-3 py-2 text-center shadow-sm backdrop-blur"
                  >
                    <div className="text-lg font-bold tabular-nums text-foreground">{s.value}</div>
                    <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">1</span>
                Shared prompts
              </span>
              <span className="text-border">→</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-[10px] font-bold text-background">2</span>
                Enable models
              </span>
            </div>
          </header>

        <Tabs defaultValue="ai-models">
          <TabsList className="grid h-auto w-full grid-cols-4 rounded-2xl border border-border/60 bg-muted/40 p-1.5">
            <TabsTrigger value="ai-models" className="flex items-center gap-1.5 rounded-xl py-2.5 text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm sm:text-sm" data-testid="tab-ai-models">
              <Brain className="w-4 h-4" />
              <span>AI Models</span>
              {stats.enabled > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 text-xs">{stats.enabled}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tts" className="flex items-center gap-1.5 rounded-xl py-2.5 text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm sm:text-sm" data-testid="tab-tts">
              <Volume2 className="w-4 h-4" />
              <span>TTS</span>
              {stats.tts > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 text-xs">{stats.tts}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="stt" className="flex items-center gap-1.5 rounded-xl py-2.5 text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm sm:text-sm" data-testid="tab-stt">
              <Mic className="w-4 h-4" />
              <span>STT</span>
              {stats.stt > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 text-xs">{stats.stt}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="gemma" className="flex items-center gap-1.5 rounded-xl py-2.5 text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm sm:text-sm" data-testid="tab-gemma">
              <Server className="w-4 h-4" />
              <span>Gemma 4</span>
              {stats.gemma > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 text-xs bg-violet-100 text-violet-700">
                  {stats.gemma}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ai-models" className="mt-6 space-y-5">
            <SharedPromptsCard
              value={config.shared_prompts}
              onSave={handleSaveSharedPrompts}
            />

            <section className="space-y-3">
              <div className="flex flex-wrap items-end justify-between gap-2 px-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-[11px] font-bold text-background">2</span>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Models</h2>
                    <p className="text-xs text-muted-foreground">
                      Tap Text / Image chips · expand a card for API key & settings
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">{stats.keyed} of {config.ai_models.length} have keys</p>
              </div>
              <ServiceSection
                items={config.ai_models}
                type="ai_models"
                onSave={makeUpdater(companyAdminCustomersAPI.updateAiModel)}
              />
            </section>
          </TabsContent>

          <TabsContent value="tts" className="mt-6">
            <ServiceSection
              items={config.tts_services}
              type="tts_services"
              onSave={makeUpdater(companyAdminCustomersAPI.updateTtsService)}
            />
          </TabsContent>

          <TabsContent value="stt" className="mt-6">
            <ServiceSection
              items={config.stt_services}
              type="stt_services"
              onSave={makeUpdater(companyAdminCustomersAPI.updateSttService)}
            />
          </TabsContent>

          <TabsContent value="gemma" className="mt-6">
            <div className="space-y-4">
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Self-Hosted Gemma 4 Servers</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Add your own Gemma AI inference servers as AI backends</p>
                </div>
                {!showAddGemma && (
                  <Button
                    data-testid="add-gemma-server-btn"
                    size="sm"
                    onClick={() => setShowAddGemma(true)}
                    className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
                  >
                    <Plus className="w-4 h-4" /> Add Server
                  </Button>
                )}
              </div>

              {/* Add form */}
              {showAddGemma && (
                <AddGemmaServerForm
                  onAdd={handleAddGemma}
                  onCancel={() => setShowAddGemma(false)}
                />
              )}

              {/* Server cards */}
              <div className="space-y-3">
                {config.gemma_servers.map(server => (
                  <GemmaServerCard
                    key={server._id}
                    server={server}
                    onSave={handleUpdateGemma}
                    onDelete={handleDeleteGemma}
                  />
                ))}
                {config.gemma_servers.length === 0 && !showAddGemma && (
                  <div className="text-center py-12 text-muted-foreground border-2 border-dashed border-border rounded-xl">
                    <Server className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm font-medium">No Gemma servers yet</p>
                    <p className="text-xs mt-1 opacity-70">Click "Add Server" to connect your self-hosted Gemma instance</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </CompanyAdminLayout>
  );
}
